import { Component, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { collection, getDocs, onSnapshot, query, QueryConstraint, where } from 'firebase/firestore';
import moment from 'moment';
import { dataStore, db } from 'src/app/app.component';
import { DateFilter, Month, User, Vehicle, Year } from 'src/app/interfaces';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatSelect, MatOption } from '@angular/material/select';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll } from '@angular/cdk/scrolling';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-audit',
  templateUrl: './audit.component.html',
  styleUrl: './audit.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatSelect,
    MatOption,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    MatProgressSpinner,
    DatePipe,
  ],
})
export class AuditComponent implements OnInit {
  filteredAudits = signal<any[]>([]);
  audits = signal<any[]>([]);
  allAudits = signal(0);
  users = signal<User[]>([]);
  vehicles = signal<Vehicle[]>([]);
  showCharacteristics = signal<boolean>(dataStore.generalSettings?.characteristicsStatus ?? false);

  dateFilter = signal<DateFilter[]>([
    { value: 'year', viewValue: 'Jaar' },
    { value: 'month', viewValue: 'Maand' },
    { value: 'week', viewValue: 'Week' },
  ]);
  years = signal<Year[]>(
    Array(11)
      .fill(0)
      .map((x, i) => {
        const year = moment().year() - i;
        return { value: year, viewValue: year.toString() };
      }),
  );
  months = signal<Month[]>([
    { value: 0, viewValue: 'Januari' },
    { value: 1, viewValue: 'Februari' },
    { value: 2, viewValue: 'Maart' },
    { value: 3, viewValue: 'April' },
    { value: 4, viewValue: 'Mei' },
    { value: 5, viewValue: 'Juni' },
    { value: 6, viewValue: 'Juli' },
    { value: 7, viewValue: 'Augustus' },
    { value: 8, viewValue: 'September' },
    { value: 9, viewValue: 'Oktober' },
    { value: 10, viewValue: 'November' },
    { value: 11, viewValue: 'December' },
  ]);
  weeks = signal<number[]>(
    Array(53)
      .fill(0)
      .map((x, i) => i + 1),
  );

  dateForm: FormGroup = this.fb.group({
    selectedDateFilter: ['month'],
    selectedYear: [],
    selectedMonth: [],
    selectedWeek: [],
  });

  searchCtrl = new UntypedFormControl();

  exporting = signal(false);
  loadingAudits = signal(false);

  constructor(private fb: FormBuilder) {}

  async ngOnInit(): Promise<void> {
    onSnapshot(
      query(collection(db, `organisations/${dataStore.selectedOrganisation.id}/users`)),
      (userSnapshot) => {
        const newUsers: User[] = [];
        userSnapshot.forEach((user) => {
          const userData = user.data() as User;
          newUsers.push({ id: user.id, ...userData } as User);
        });
        this.users.set(newUsers);
      },
    );

    onSnapshot(
      query(collection(db, `organisations/${dataStore.selectedOrganisation.id}/vehicles`)),
      (vehicleSnapshot) => {
        const newVehicles: Vehicle[] = [];
        vehicleSnapshot.forEach((vehicle) => {
          const vehicleData = vehicle.data() as Vehicle;
          newVehicles.push({ id: vehicle.id, ...vehicleData } as Vehicle);
        });
        this.vehicles.set(newVehicles);
      },
    );

    this.years().forEach((year) => {
      if (year.value === parseInt(moment().format('Y'))) {
        this.dateForm.controls.selectedYear.setValue(year.value);
      }
    });
    this.months().forEach((month) => {
      if (month.value === parseInt(moment().format('M'))) {
        this.dateForm.controls.selectedMonth.setValue(month.value - 1);
      }
    });
    this.weeks().forEach((week) => {
      if (week === parseInt(moment().format('W'))) {
        this.dateForm.controls.selectedWeek.setValue(week);
      }
    });
    this.dateForm.patchValue({
      selectedYear: moment().year(),
      selectedMonth: moment().month(),
      selectedWeek: moment().isoWeek(),
    });

    this.searchCtrl.valueChanges.subscribe((value) => {
      this.filterAudits(value);
    });

    await this.periodFilter();
  }

  async periodFilter() {
    if (this.loadingAudits()) {
      return;
    }
    this.loadingAudits.set(true);
    this.dateForm.disable();
    const queryConstraints = [];
    switch (this.dateForm.value.selectedDateFilter) {
      case 'year':
        queryConstraints.push(where('year', '==', this.dateForm.value.selectedYear));
        break;
      case 'month':
        queryConstraints.push(where('year', '==', this.dateForm.value.selectedYear));
        queryConstraints.push(where('month', '==', this.dateForm.value.selectedMonth));
        break;
      case 'week':
        const weekString = `${this.dateForm.value.selectedYear}-${this.dateForm.value.selectedWeek}`;
        queryConstraints.push(where('week', '==', weekString));
        break;
    }
    await this.getAudits(queryConstraints);
    this.filterAudits(this.searchCtrl.value ?? '');
    this.loadingAudits.set(false);
    this.dateForm.enable();
  }

  async getAudits(queryConstraints: QueryConstraint[]) {
    const paymentDocs = await getDocs(
      query(
        collection(db, `organisations/${dataStore.selectedOrganisation.id}/payments`),
        ...queryConstraints,
      ),
    );
    const chargeDocs = await getDocs(
      query(
        collection(db, `organisations/${dataStore.selectedOrganisation.id}/charges`),
        ...queryConstraints,
      ),
    );
    const auditsArray: any[] = [];
    chargeDocs.forEach((chargeDoc) => {
      const charge = chargeDoc.data();
      charge.id = chargeDoc.id;
      this.users().forEach(async (user) => {
        if (user.id === charge.userRef.id) {
          charge.userData = user;
          auditsArray.push(charge);
        }
      });
    });
    paymentDocs.forEach((paymentDoc) => {
      const payment = paymentDoc.data();
      payment.id = paymentDoc.id;
      this.users().forEach((user) => {
        if (user.id === payment.userRef.id) {
          payment.userData = user;
          auditsArray.push(payment);
        }
      });
    });
    this.audits.set(auditsArray);
  }

  filterAudits(searchQuery) {
    if (!searchQuery || searchQuery === '') {
      this.filteredAudits.set([...this.audits()]);
    } else {
      const filteredAudits = [];
      this.audits().forEach((audit) => {
        let passesSearchFilter = false;
        if (audit.userData.name) {
          if (audit.userData.name.toLowerCase().includes(searchQuery.toLowerCase())) {
            passesSearchFilter = true;
          }
        }
        if (audit.actionBy) {
          if (audit.actionBy.toLowerCase().includes(searchQuery.toLowerCase())) {
            passesSearchFilter = true;
          }
        }
        if (audit.description) {
          if (audit.description.toLowerCase().includes(searchQuery.toLowerCase())) {
            passesSearchFilter = true;
          }
        }
        if (audit.remarks) {
          if (audit.remarks.toLowerCase().includes(searchQuery.toLowerCase())) {
            passesSearchFilter = true;
          }
        }
        if (audit.vehicleName) {
          if (audit.vehicleName.toLowerCase().includes(searchQuery.toLowerCase())) {
            passesSearchFilter = true;
          }
        }
        if (audit.characteristics) {
          audit.characteristics?.forEach((characteristic: string) => {
            if (characteristic.toLowerCase().includes(searchQuery.toLowerCase())) {
              passesSearchFilter = true;
            }
          });
        }
        if (passesSearchFilter) {
          filteredAudits.push(audit);
        }
      });
      this.filteredAudits.set(filteredAudits);
    }
    this.allAudits.set(this.filteredAudits().length);
    this.sortAudits();
  }

  sortAudits() {
    this.filteredAudits.update((audits) =>
      [...audits].sort((a, b) => {
        if (a.date < b.date) {
          return 1;
        }
        if (a.date > b.date) {
          return -1;
        }
        return 0;
      }),
    );
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  getFilteredAudits() {
    return { filteredAudits: this.filteredAudits() };
  }

  getVehicleName(vehicleId: string) {
    const vehicle = this.vehicles().find((v) => v.id === vehicleId);
    return vehicle ? vehicle.name : '';
  }
}
